import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { object, string, bool, array, number } from "yup";
import { useIntl } from "react-intl";
import AdminFormModal from "../../AdminFormModal";
import { Form, Button, Row, Space } from "@masterteam/mt-design-react";

import Upload from "../../Upload";
import Values from "../Values";
import api from "../../../providers/api";
import { addToast } from "../../../helpers";
import { updateStrategy } from "../../../reducers/strategy";

import { ReactComponent as StrategicIcon } from "../../../assets/images/strategic-strategy.svg";
import { ReactComponent as SupportiveIcon } from "../../../assets/images/supportive-strategy.svg";

import "./style.scss";

const { Field } = Form;

const validationSchema = object().shape({
  StrategyType: number().required(" "),
  EnglishName: string().required(" "),
  ArabicName: string().required(" "),
  EnglishVision: string().required(" "),
  ArabicVision: string().required(" "),
  EnglishMission: string().required(" "),
  ArabicMission: string().required(" "),
  MainStrategy: bool(),
  Values: array()
    .of(object())
    .when("StrategyType", {
      is: (StrategyType) => +StrategyType === 1,
      then: (schema) => schema.min(1, " ")
    })
});

const StrategyForm = ({ data, closeModal }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const isAr = intl.locale === "ar";
  const [strategyTypes] = useSelector(({ theme }) => [
    theme.StrategyTypes.item.Items
  ]);
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState({
    StrategyType: 1,
    MainStrategy: false,
    EnglishName: "",
    ArabicName: "",
    EnglishVision: "",
    ArabicVision: "",
    EnglishMission: "",
    ArabicMission: "",
    Values: []
  });

  const getStrategyData = () => {
    setLoading(true);
    api.strategy
      .getById(data?.ID)
      .then((res) => {
        setInitialValues({
          ...res.Data,
          Values: res?.Data?.Values?.map((e, i) => {
            return { ...e, index: i };
          })
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (data) {
      getStrategyData();
    }
  }, []);

  const getAttachment = async (item) => {
    const { Data: attachment } = await api.attachment.create(item.Attachment);
    if (!attachment) throw new Error("upload failed!");

    return {
      ...item,
      Attachment: { ID: attachment[0] }
    };
  };

  const onSubmit = async (values, { setSubmitting }) => {
    try {
      let oldValues = [];
      let newValues = [];
      if (values?.Values?.length) {
        values?.Values?.forEach((item) =>
          item.Attachment.ID ? oldValues.push(item) : newValues.push(item)
        );
      }

      if (newValues?.length) {
        newValues = await Promise.all(newValues.map(getAttachment));
      }

      const body = {
        ...values,
        Values: [...oldValues, ...newValues]
      };
      const service = data ? "update" : "create";
      const res = await api.strategy[service](body);
      const { Data, StatusCode } = res;
      if (StatusCode === "Success") {
        dispatch(updateStrategy(Data));
        addToast.success(data ? "toast.successUpdate" : "toast.successSave");
        closeModal();
      }
    } catch (error) {
      addToast.error(error?.message, {
        toastId: "get-users-error"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const checkIsMainStrategy = async () => {
    try {
      const res = await api.strategy.validateMainStrategy(data?.ID ?? 0);
      return res.Data;
    } catch (err) {
      addToast.error(err.message, {
        toastId: "get-users-error"
      });
    }
  };

  return (
    <AdminFormModal
      title={intl.formatMessage({
        id: data ? "common.update-strategy" : "common.add-strategy"
      })}
      isLoading={loading}
      onClose={closeModal}
    >
      <Form
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ values, errors, isSubmitting, isValid, setFieldValue }) => (
          <>
            <Space
              direction="vertical"
              size="medium"
              className="strategy-form-body"
            >
              <Space direction="vertical" className="form-container">
                <Space size="small" align="center">
                  <Space direction="vertical" className="strategy-types">
                    <Space gap="3px">
                      {intl.formatMessage({ id: "strategy.strategy-type" })}
                      <span className="asterisk">*</span>
                    </Space>

                    <Space size="medium">
                      {strategyTypes?.map((item) => (
                        <Space
                          align="center"
                          className={`strategy-types__item ${
                            +item.Value === +values?.StrategyType
                              ? "active-item"
                              : ""
                          }`}
                          onClick={() => {
                            setFieldValue("StrategyType", +item?.Value);
                            if (+item.Value === 2)
                              setFieldValue("MainStrategy", false);
                          }}
                        >
                          {item?.Value === "1" ? (
                            <StrategicIcon />
                          ) : (
                            <SupportiveIcon />
                          )}
                          <div>{isAr ? item?.Arabic : item?.English}</div>
                        </Space>
                      ))}
                    </Space>
                  </Space>

                  {+values?.StrategyType === 1 ? (
                    <div style={{ flex: 1 }}>
                      <Field
                        name="MainStrategy"
                        type="checkbox"
                        label={intl.formatMessage({
                          id: "common.main-strategy"
                        })}
                        inputProps={{
                          highContrast: true,
                          onChange: async (val) => {
                            if (values.MainStrategy)
                              setFieldValue(
                                "MainStrategy",
                                !values.MainStrategy
                              );
                            else {
                              const valid = await checkIsMainStrategy();
                              if (valid) {
                                setFieldValue(
                                  "MainStrategy",
                                  !values.MainStrategy
                                );
                              } else {
                                addToast.error("error.main-strategy");
                              }
                            }
                          },
                          placeholder: intl.formatMessage({
                            id: "common.main-strategy"
                          })
                        }}
                        hideLabel
                        hideErrorMessage
                      />
                    </div>
                  ) : null}
                </Space>

                <Row size="small">
                  <Field
                    name="ArabicName"
                    label={intl.formatMessage({ id: "strategy.arabic-title" })}
                    inputProps={{
                      maxLength: 256,
                      placeholder: intl.formatMessage({
                        id: "common.arabic-name"
                      })
                    }}
                    required
                  />

                  <Field
                    name="EnglishName"
                    label={intl.formatMessage({ id: "strategy.english-title" })}
                    inputProps={{
                      maxLength: 256,
                      placeholder: intl.formatMessage({
                        id: "common.english-name"
                      })
                    }}
                    required
                  />
                </Row>

                <Row size="small">
                  <Field
                    type="textarea"
                    name="ArabicVision"
                    label={intl.formatMessage({
                      id: "strategy.arabic-vision"
                    })}
                    inputProps={{
                      maxLength: 150,
                      placeholder: intl.formatMessage({
                        id: "strategy.arabic-vision"
                      })
                    }}
                    required
                  />

                  <Field
                    type="textarea"
                    name="EnglishVision"
                    label={intl.formatMessage({
                      id: "strategy.english-vision"
                    })}
                    inputProps={{
                      maxLength: 150,
                      placeholder: intl.formatMessage({
                        id: "strategy.english-vision"
                      })
                    }}
                    required
                  />
                </Row>

                <Row size="small">
                  <Field
                    type="textarea"
                    name="ArabicMission"
                    label={intl.formatMessage({
                      id: "strategy.arabic-mission"
                    })}
                    inputProps={{
                      maxLength: 300,
                      placeholder: intl.formatMessage({
                        id: "strategy.arabic-mission"
                      })
                    }}
                    required
                  />
                  <Field
                    type="textarea"
                    name="EnglishMission"
                    label={intl.formatMessage({
                      id: "strategy.english-mission"
                    })}
                    inputProps={{
                      maxLength: 300,
                      placeholder: intl.formatMessage({
                        id: "strategy.english-mission"
                      })
                    }}
                    required
                  />
                </Row>

                <Values />
              </Space>

              <Row justify="end" className="form-footer">
                <Button
                  loading={isSubmitting}
                  disabled={!isValid}
                  variant="primary"
                  type="submit"
                  fixed
                >
                  {intl.formatMessage({
                    id: data ? "common.update" : "common.add"
                  })}
                </Button>
              </Row>
            </Space>
          </>
        )}
      </Form>
    </AdminFormModal>
  );
};

export default StrategyForm;
